import { forwardRef, useMemo } from "react";
import type { PostedOrder } from "~/features/posted-orders/types";
import { OutcomeBadge, getOutcomeLabel } from "~/features/orders/components/OutcomeBadge";
import { formatDateTimeFromISO, orDoubleDash } from "~/shared/utils/formatting";
import { InlineFilterDropdown } from "~/shared/components/InlineFilterDropdown";
import { GlobalSearchBar } from "~/shared/components/GlobalSearchBar";

type PostedOrdersTableProps = Readonly<{
  items: PostedOrder[];
  loading: boolean;
  onFailedItemClick?: (item: PostedOrder) => void;
  searchQuery?: string;
  onSearchChange?: (value: string) => void;
  onSearchSubmit?: (value: string) => void;
  cityFilter?: string[];
  onCityFilterChange?: (selected: string[]) => void;
  cityOptions?: string[];
  outcomeFilter?: string[];
  onOutcomeFilterChange?: (selected: string[]) => void;
  navStatusFilter?: string[];
  onNavStatusFilterChange?: (selected: string[]) => void;
  whiteGloveFilter?: string[];
  onWhiteGloveFilterChange?: (selected: string[]) => void;
}>;

const getNavStatusBadgeClass = (status?: string | null) => {
  const normalized = (status ?? "").toLowerCase();
  switch (normalized) {
    case "success":
      return "badge-success";
    case "failed":
      return "badge-error";
    case "pending":
      return "badge-warning";
    default:
      return "badge-default";
  }
};

const getNavStatusLabel = (status?: string | null): string => {
  const normalized = (status ?? "").toLowerCase();
  switch (normalized) {
    case "pending":
      return "Pending";
    case "success":
      return "Success";
    case "failed":
      return "Failed";
    default:
      return status?.trim() ? status : "—";
  }
};

export const PostedOrdersTable = forwardRef<HTMLDivElement, PostedOrdersTableProps>(function PostedOrdersTable({
  items,
  loading,
  onFailedItemClick,
  searchQuery = "",
  onSearchChange,
  onSearchSubmit,
  cityFilter = [],
  onCityFilterChange,
  cityOptions,
  outcomeFilter = [],
  onOutcomeFilterChange,
  navStatusFilter = [],
  onNavStatusFilterChange,
  whiteGloveFilter = [],
  onWhiteGloveFilterChange,
}, ref) {
  // Apply outcome + navStatus + whiteGlove filters client-side; options derived from all items so deselecting works
  const displayedItems = useMemo(() => {
    let result = items;
    if (outcomeFilter.length > 0)
      result = result.filter(item => item.outcome && outcomeFilter.includes(item.outcome));
    if (navStatusFilter.length > 0)
      result = result.filter(item => navStatusFilter.includes(getNavStatusLabel(item.navStatus)));
    if (whiteGloveFilter.length > 0)
      result = result.filter(item => whiteGloveFilter.includes(item.whiteGlove ? 'Yes' : 'No'));
    return result;
  }, [items, outcomeFilter, navStatusFilter, whiteGloveFilter]);

  const isEmpty = !loading && displayedItems.length === 0;
  const hasActiveFilter = cityFilter.length > 0 || outcomeFilter.length > 0 || navStatusFilter.length > 0 || whiteGloveFilter.length > 0;

  // Extract unique filter options from all items (unfiltered) so the dropdown stays populated
  const filterOptions = useMemo(() => {
    const outcomes = new Set<string>();
    const navStatuses = new Set<string>();
    const whiteGloves = new Set<string>();
    items.forEach(item => {
      if (item.outcome) outcomes.add(item.outcome);
      if (item.navStatus) navStatuses.add(getNavStatusLabel(item.navStatus));
      whiteGloves.add(item.whiteGlove ? 'Yes' : 'No');
    });
    const alpha = (a: string, b: string) => a.localeCompare(b);
    return {
      outcomes: Array.from(outcomes).sort(alpha),
      cities: cityOptions && cityOptions.length > 0 ? cityOptions : (items.map(i => i.city).filter((c): c is string => Boolean(c)).sort(alpha)),
      navStatuses: Array.from(navStatuses).sort(alpha),
      whiteGloves: Array.from(whiteGloves).sort(alpha),
    };
  }, [items, cityOptions]);

  return (
    <div
      className="table-container table-container--scroll-inner"
      style={{ display: 'flex', flexDirection: 'column', width: '100%', flex: isEmpty ? '0 0 auto' : 1, minHeight: 0, position: 'relative' }}
    >
      {loading && items.length > 0 && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(255,255,255,0.7)',
            zIndex: 20,
          }}
        >
          <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>Loading…</p>
        </div>
      )}
      <div
        style={{
          padding: '16px',
          borderBottom: '1px solid var(--border)',
          background: 'var(--neutral-gray)',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          flexShrink: 0,
        }}
      >
        <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}>Posted Orders</h3>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 24,
            height: 24,
            borderRadius: '50%',
            background: 'var(--primary-blue)',
            color: 'white',
            fontSize: 12,
            fontWeight: 600,
          }}
        >
          {displayedItems.length}
        </span>
        <div style={{ flex: 1 }} />
        <GlobalSearchBar
          value={searchQuery}
          onChange={onSearchChange ?? (() => { })}
          onSubmit={onSearchSubmit ?? (() => { })}
          placeholder="Search by SM#, patient, patient ID, city…"
        />
      </div>
      <div
        ref={ref}
        className="table-scroll-wrapper"
        style={{ flex: isEmpty ? '0 0 auto' : 1, minHeight: 0, overflowY: isEmpty ? 'hidden' : 'auto', position: 'relative' }}
      >
        <table className="table">
          <thead>
            <tr>
              <th>When</th>
              <th>SM Number</th>
              <th>Patient</th>
              <th>Patient Id</th>
              <th>
                <InlineFilterDropdown
                  label="Outcome"
                  options={filterOptions.outcomes}
                  selected={outcomeFilter}
                  onChange={onOutcomeFilterChange ?? (() => { })}
                  formatOption={getOutcomeLabel}
                />
              </th>
              <th>
                <InlineFilterDropdown
                  label="NAV Status"
                  options={filterOptions.navStatuses}
                  selected={navStatusFilter}
                  onChange={onNavStatusFilterChange ?? (() => { })}
                />
              </th>
              <th>
                <InlineFilterDropdown
                  label="City"
                  options={filterOptions.cities}
                  selected={cityFilter}
                  onChange={onCityFilterChange ?? (() => { })}
                  searchable
                />
              </th>
              <th>
                <InlineFilterDropdown
                  label="White Glove"
                  options={filterOptions.whiteGloves}
                  selected={whiteGloveFilter}
                  onChange={onWhiteGloveFilterChange ?? (() => { })}
                />
              </th>
              <th style={{ width: 100 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {isEmpty && (
              <tr>
                <td colSpan={9} style={{ textAlign: 'center', padding: 0, color: 'var(--text-muted)', borderBottom: 'none' }}>
                  <div
                    style={{
                      minHeight: 120,
                      padding: '16px 24px',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 12,
                    }}
                  >
                    <div style={{ fontWeight: 600, fontSize: 14 }}>
                      {hasActiveFilter ? 'No orders match the selected filters' : 'No posted orders found'}
                    </div>
                  </div>
                </td>
              </tr>
            )}
            {!loading && displayedItems.map((item, index) => {
              const isFailed = item.navStatus?.toLowerCase() === "failed";
              const createdDateTime = item.created ? formatDateTimeFromISO(item.created) : null;
              return (
                <tr
                  key={item.smNumber + (item.created ?? "") + index}
                  data-sm-number={item.smNumber}
                  className={isFailed ? "posted-orders-table__row--failed" : undefined}
                >
                  <td>{createdDateTime ? `${createdDateTime.date} ${createdDateTime.time}` : orDoubleDash(null)}</td>
                  <td><strong>{item.smNumber}</strong></td>
                  <td>{orDoubleDash(item.patientName)}</td>
                  <td>{orDoubleDash(item.patientId)}</td>
                  <td>
                    <OutcomeBadge outcome={item.outcome} />
                  </td>
                  <td>
                    <span className={`badge ${getNavStatusBadgeClass(item.navStatus)}`}>
                      {getNavStatusLabel(item.navStatus)}
                    </span>
                  </td>
                  <td>{orDoubleDash(item.city)}</td>
                  <td>{item.whiteGlove ? 'Yes' : 'No'}</td>
                  <td>
                    {isFailed && onFailedItemClick && (
                      <button
                        type="button"
                        className="secondary"
                        onClick={(e) => {
                          e.stopPropagation();
                          onFailedItemClick(item);
                        }}
                        style={{ padding: "6px 12px", fontSize: 12 }}
                      >
                        View & Retry
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <style>{`
        .badge {
          display: inline-block;
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 12px;
          font-weight: 500;
          text-transform: capitalize;
        }
        .badge-success {
          background: #E8F5E9;
          color: #2E7D32;
        }
        .badge-warning {
          background: #FFF3E0;
          color: #E65100;
        }
        .badge-error {
          background: #FFEBEE;
          color: #C62828;
        }
        .badge-default {
          background: #F5F5F5;
          color: #616161;
        }
        .posted-orders-table__row--failed {
          background: #FFF8F8;
        }
      `}</style>
    </div>
  );
});
